/**
 * In-place help (DESIGN UD-18): `<Term k="kappa">data weight κ</Term>` and `<ChannelTerm id="LX-01" />` render a
 * dotted-underline button that opens a small popover with the glossary entry or the channel explainer. Opens on click
 * or Enter/Space (stays until closed), and on hover or focus after a short delay (closes on leave). Escape closes and
 * returns focus. One popover is open at a time. Text only: Preact text nodes, never HTML strings.
 */
import { signal } from '@preact/signals';
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { explainerFor } from '../../engine/channels/explain.ts';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { GlossaryKey } from '../../shared/help-types.ts';
import { hashFor } from '../router.ts';
import { result } from '../state.ts';
import { glossaryFor } from '../strings-glossary.ts';
import { HELP } from '../strings-help.ts';

/** The id of the one open popover, or null. */
const openId = signal<string | null>(null);

const HOVER_DELAY_MS = 250;
const LEAVE_GRACE_MS = 150;
const EDGE = 8;
const GAP = 6;

let seq = 0;

/**
 * Place a fixed-position element below its anchor, clamped inside the viewport. It flips above when there is no room
 * below; when it fits on neither side it takes the larger side and scrolls inside itself.
 */
function place(pop: HTMLElement, anchor: HTMLElement): void {
	pop.style.maxHeight = '';
	const a = anchor.getBoundingClientRect();
	const w = pop.offsetWidth;
	const h = pop.offsetHeight;
	const vw = document.documentElement.clientWidth;
	const vh = window.innerHeight;
	const below = vh - a.bottom - GAP - EDGE;
	const above = a.top - GAP - EDGE;
	const left = Math.max(EDGE, Math.min(a.left, vw - w - EDGE));
	let top: number;
	if (h <= below) top = a.bottom + GAP;
	else if (h <= above) top = a.top - GAP - h;
	else if (below >= above) {
		top = a.bottom + GAP;
		pop.style.maxHeight = `${Math.max(120, below)}px`;
	} else {
		const room = Math.max(120, above);
		top = Math.max(EDGE, a.top - GAP - room);
		pop.style.maxHeight = `${room}px`;
	}
	pop.style.left = `${Math.round(left)}px`;
	pop.style.top = `${Math.round(top)}px`;
	pop.style.visibility = 'visible';
}

/** The popover box: fixed position, measured and clamped to the viewport; it follows the anchor on scroll and resize. */
export function Popover({
	id,
	anchor,
	children,
	onEnter,
	onLeave,
}: {
	id: string;
	anchor: HTMLElement | null;
	children: ComponentChildren;
	onEnter: () => void;
	onLeave: () => void;
}): JSX.Element {
	const ref = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const pop = ref.current;
		if (!pop || !anchor) return;
		place(pop, anchor);
		const again = () => place(pop, anchor);
		window.addEventListener('scroll', again, { capture: true, passive: true });
		window.addEventListener('resize', again);
		return () => {
			window.removeEventListener('scroll', again, { capture: true });
			window.removeEventListener('resize', again);
		};
	}, [anchor]);
	return (
		<div
			ref={ref}
			id={id}
			role="tooltip"
			class="popover"
			style={{ visibility: 'hidden' }}
			onMouseEnter={onEnter}
			onMouseLeave={onLeave}
			onFocusIn={onEnter}
			onFocusOut={onLeave}
		>
			{children}
		</div>
	);
}

/** Behaviour shared by `Term` and `ChannelTerm`. */
function useTrigger() {
	const id = useRef(`pop-${++seq}`).current;
	const btn = useRef<HTMLButtonElement>(null);
	const timer = useRef(0);
	const pinned = useRef(false);
	const clear = () => {
		if (timer.current) window.clearTimeout(timer.current);
		timer.current = 0;
	};
	const isOpen = openId.value === id;
	const close = (refocus = false) => {
		clear();
		pinned.current = false;
		if (openId.value === id) openId.value = null;
		if (refocus) btn.current?.focus();
	};
	const openSoon = () => {
		clear();
		if (isOpen) return;
		timer.current = window.setTimeout(() => {
			timer.current = 0;
			openId.value = id;
		}, HOVER_DELAY_MS);
	};
	const leaveSoon = () => {
		clear();
		if (pinned.current) return;
		timer.current = window.setTimeout(() => {
			timer.current = 0;
			if (!pinned.current && openId.value === id) openId.value = null;
		}, LEAVE_GRACE_MS);
	};
	const stay = () => clear();
	const onClick = () => {
		clear();
		if (isOpen && pinned.current) close();
		else {
			pinned.current = true;
			openId.value = id;
		}
	};
	// another popover opened: forget the pin
	if (!isOpen) pinned.current = false;
	// a layout effect: the Escape listener must exist in the same frame the popover first shows
	// biome-ignore lint/correctness/useExhaustiveDependencies: close acts on refs and the shared signal only
	useLayoutEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			e.stopPropagation();
			close(true);
		};
		const away = (e: MouseEvent) => {
			const t = e.target as Node;
			if (btn.current?.contains(t)) return;
			if ((t as Element).closest?.('.popover')) return;
			close();
		};
		document.addEventListener('keydown', onKey, true);
		document.addEventListener('mousedown', away);
		return () => {
			document.removeEventListener('keydown', onKey, true);
			document.removeEventListener('mousedown', away);
		};
	}, [isOpen]);
	useEffect(
		() => () => {
			if (timer.current) window.clearTimeout(timer.current);
			if (openId.value === id) openId.value = null;
		},
		[id]
	);
	const button = (children: ComponentChildren, label?: string) => (
		<button
			type="button"
			class="term"
			ref={btn}
			aria-expanded={isOpen}
			aria-describedby={isOpen ? id : undefined}
			aria-label={label}
			onClick={onClick}
			onMouseEnter={openSoon}
			onMouseLeave={leaveSoon}
			onFocus={openSoon}
			onBlur={leaveSoon}
		>
			{children}
		</button>
	);
	return { id, isOpen, btn, button, stay, leaveSoon, close };
}

/** A glossary term, defined in place. Renders only its children when the key has no entry. */
export function Term({
	k,
	children,
}: { k: GlossaryKey; children: ComponentChildren }): JSX.Element {
	const t = useTrigger();
	const entry = glossaryFor(k);
	if (!entry) return <>{children}</>;
	return (
		<>
			{t.button(children)}
			{t.isOpen ? (
				<Popover id={t.id} anchor={t.btn.current} onEnter={t.stay} onLeave={t.leaveSoon}>
					<strong class="popover-term">{entry.term}</strong>
					<p>{entry.definition}</p>
					{entry.use ? <p class="popover-use">{entry.use}</p> : null}
					<a
						class="popover-link"
						href={hashFor('glossary', { anchor: k })}
						onClick={() => t.close()}
					>
						{HELP.glossaryLink}
					</a>
				</Popover>
			) : null}
		</>
	);
}

/** The family label as shown in the header ("VOCAB" → "Vocab"). */
export function familyLabel(family: string): string {
	return family.charAt(0) + family.slice(1).toLowerCase();
}

/** The channel explainer lines, shared by the popover and the Glossary view. */
export function ExplainerLines({ id }: { id: ChannelId }): JSX.Element | null {
	const x = explainerFor(id);
	if (!x) return null;
	return (
		<dl class="explainer">
			<dt>{HELP.measures}</dt>
			<dd>{x.measures}</dd>
			<dt>{HELP.why}</dt>
			<dd>{x.why}</dd>
			<dt>{HELP.direction}</dt>
			<dd>{x.direction === 'higher' ? HELP.directionHigher : HELP.directionLower}</dd>
			<dt>{HELP.humanCauses}</dt>
			<dd>{x.humanCauses}</dd>
			<dt>{HELP.needs}</dt>
			<dd>{x.needs}</dd>
		</dl>
	);
}

/** The name and family of a channel, from the result when there is one. */
export function channelMeta(id: ChannelId): { name?: string; family?: string } {
	const c = result.value?.channels.find((ch) => ch.id === id);
	return c ? { name: c.name, family: c.family } : {};
}

/** A channel ID (optionally with its name) that opens the channel's explainer. */
export function ChannelTerm({ id, name }: { id: ChannelId; name?: string }): JSX.Element {
	const t = useTrigger();
	const x = explainerFor(id);
	const label = name ? `${id} ${name}` : id;
	if (!x) return <>{label}</>;
	const meta = channelMeta(id);
	const header = [id, name ?? meta.name, meta.family ? familyLabel(meta.family) : undefined]
		.filter(Boolean)
		.join(' · ');
	return (
		<>
			{t.button(label)}
			{t.isOpen ? (
				<Popover id={t.id} anchor={t.btn.current} onEnter={t.stay} onLeave={t.leaveSoon}>
					<strong class="popover-term">{header}</strong>
					<ExplainerLines id={id} />
					<a class="popover-link" href={hashFor('channel', { id })} onClick={() => t.close()}>
						{HELP.openChannel}
					</a>
				</Popover>
			) : null}
		</>
	);
}
